import { useEffect, useRef } from 'react';
import PhotoSphere from '@/common/components/photo-sphere';
import Spacer from '@/common/components/spacer';
import { VehicleImageWithMeta } from '@/catalog/types/vehicle';
import { createImageUrl } from '@/catalog/utils/pdp/create-image-url';

interface ImageGalleryScrollProps {
  images: VehicleImageWithMeta[];
  interiorImages: VehicleImageWithMeta[];
  exteriorImages: VehicleImageWithMeta[];
  selectedImageIndex: number;
  setSelectedImageIndex: (index: number) => void;
  cdnUrl: string;
}

export default function ImageGalleryScroll({
  images,
  interiorImages,
  exteriorImages,
  selectedImageIndex,
  cdnUrl,
}: ImageGalleryScrollProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!containerRef.current) return;

    const targetElement = imageRefs.current[selectedImageIndex];

    if (targetElement) {
      setTimeout(() => {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }
  }, [selectedImageIndex]);

  return (
    <div
      ref={containerRef}
      className="scrollbar-hide h-full w-full overflow-y-scroll px-[16px] md:px-[24px]"
    >
      {/* Using spacers to not cut off images during scroll */}
      <Spacer variant="height48" hideForBreakpoints={['md']} />
      <Spacer variant="height56" hideForBreakpoints={['sm']} />

      {exteriorImages?.length > 0 ? (
        <>
          <h2 className="text-flexcar-neutral100">Exterior</h2>
          <Spacer variant="height24" />
          <div className="flex flex-col gap-[16px]">
            {exteriorImages.map((image) => {
              const url = createImageUrl(cdnUrl, image);
              const imageIndex = images.findIndex((img) => img === image);

              return (
                <div
                  key={`gallery-image-${image.image_type}`}
                  ref={(el) => {
                    if (el) imageRefs.current[imageIndex] = el;
                  }}
                  id={`gallery-image-${image.image_type}`}
                >
                  <img
                    src={url}
                    alt={`Gallery Vehicle ${image.image_type}`}
                    fetchPriority="high"
                    loading="eager"
                    className="rounded-[16px]"
                  />
                </div>
              );
            })}
          </div>
          <Spacer variant="height40" />
        </>
      ) : null}

      {interiorImages?.length > 0 ? (
        <>
          <h2 className="text-flexcar-neutral100">Interior</h2>
          <Spacer variant="height24" />

          <div className="flex flex-col gap-[16px]">
            {interiorImages.map((image) => {
              const isInterior360 = image.image_category === '360int';
              const url = createImageUrl(cdnUrl, image);
              const imageIndex = images.findIndex((img) => img === image);

              return (
                <div
                  key={`gallery-image-${image.image_type}`}
                  ref={(el) => {
                    if (el) imageRefs.current[imageIndex] = el;
                  }}
                  id={`gallery-image-${image.image_type}`}
                >
                  {isInterior360 ? (
                    <div className="aspect-[4/3] h-fit w-full overflow-hidden rounded-[16px]">
                      <PhotoSphere url={url} fullscreenEventDelegate={() => {}} />
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt={`Gallery Vehicle ${image.image_type}`}
                      fetchPriority="high"
                      loading="eager"
                      className="rounded-[16px]"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : null}
      <Spacer variant="height48" hideForBreakpoints={['md']} />
      <Spacer variant="height56" hideForBreakpoints={['sm']} />
    </div>
  );
}
